import { EApportionmentType } from "api/enums/EApportionmentType";
import {
  NoItemsToBillingException,
  SendToBillingException,
} from "api/exceptions/MeasurementException";
import { ICityItemToBilling, IProductItemToBilling } from "api/interfaces";
import {
  IBillingData,
  IBillingDataItem,
  IBranch,
  IClient,
  ICostCenter,
  IDocumentType,
  IPaymentCondition,
  IProject,
} from "api/interfaces/megaERP";
import MeasurementModel from "api/models/MeasurementModel";
import { formatNumber, isEmptyObject } from "utils";
import { decimal, percentualToValue, times } from "utils/DecimalPrecision";
import MegaERPService from "./MegaERPService";

class SendToBillingService {
  /**
   * Valida e monta os dados necessários para enviar a medição
   * para faturamento no ERP Mega. Quando a API retorna que o pedido
   * foi gerado com sucesso, é atualizado os dados do pedido
   * no documento da medição.
   */
  async prepareAndSendToBilling(
    api: MegaERPService,
    measurement: MeasurementModel,
    items: IBillingDataItem[],
    data: {
      client?: IClient;
      branch?: IBranch;
      paymentCondition?: IPaymentCondition;
      documentType?: IDocumentType;
      costCenter?: ICostCenter;
      projectErp?: IProject;
    }
  ): Promise<{ orderId: number; data: IBillingData }> {
    if (!items?.length) throw new NoItemsToBillingException();

    const dataValidated = this.getDataToSend(data);
    if (!dataValidated) throw new SendToBillingException();

    const clientPattern = await api.getClientPattern(dataValidated.branch.id);
    const observation = this.createObservationToSend(measurement);
    const dataToSend: IBillingData = {
      ...dataValidated,
      issueDate: new Date(),
      items,
      observation,
      clientPattern,
      measurementTotal: measurement.getReadjustedTotal(),
      measurementBillingsTotal: measurement.getBillingsTotal(),
      measurementNetTotal: measurement.getNetTotal(),
    };

    return api.sendToBilling(dataToSend).then((orderId) => {
      if (!orderId) throw new SendToBillingException();
      return { orderId, data: dataToSend };
    });
  }

  /** Monta o objeto com os dados necessários para envio. */
  private getDataToSend(data: {
    client?: IClient;
    branch?: IBranch;
    paymentCondition?: IPaymentCondition;
    documentType?: IDocumentType;
    costCenter?: ICostCenter;
    projectErp?: IProject;
  }) {
    if (isEmptyObject(data)) return;
    if (!data.branch) return;
    if (!data.paymentCondition) return;
    if (!data.documentType) return;
    if (!data.costCenter) return;
    if (!data.projectErp) return;
    if (!data.client) return;

    return {
      branch: data.branch,
      paymentCondition: data.paymentCondition,
      documentType: data.documentType,
      client: data.client,
      costCenter: data.costCenter,
      project: data.projectErp,
    };
  }

  /** Monta o objeto com os itens a serem enviados, concatenando
   * os itens de produtos escolhidos com os municípios escolhidos e
   * com os valores de rateio, descontos e caução calculados.
   */
  createItemsToSend(
    measurement: MeasurementModel,
    productsItems: IProductItemToBilling[],
    citiesItems: ICityItemToBilling[],
    separateReadjust: boolean = false
  ): IBillingDataItem[] {
    const items: IBillingDataItem[] = [];
    const itensTotal = productsItems.length * citiesItems.length;
    const values = this.calculateValuesToSend(
      measurement,
      separateReadjust ? itensTotal * 2 : itensTotal
    );

    for (const product of productsItems) {
      const productData = {
        codItem: product.codItem,
        pattern: product.pattern,
        unit: product.unit,
        materialUsed: product.materialUsed,
        discount: values.discount,
        retention: values.retention,
        productName: product.description,
        cityName: "",
      };

      for (const city of citiesItems) {
        productData.cityName = city.city?.name || "";
        const description = `${product.description} - ${city.city?.name}`;

        if (separateReadjust) {
          const { measurementUnitCost, readjustUnitCost } =
            this.calculateItemWithSeparateReadjust(
              product.measurementValue,
              city.apportionmentPercentage,
              measurement.getReadjustedPercentage()
            );

          items.push({
            ...productData,
            description: `${description} (Medição)`,
            unitCost: measurementUnitCost,
            apportionmentType: EApportionmentType.MEASUREMENT,
          });

          items.push({
            ...productData,
            description: `${description} (Reajuste)`,
            unitCost: readjustUnitCost,
            apportionmentType: EApportionmentType.READJUSTMENT,
          });
        } else {
          const measurementUnitCost = this.calculateItem(
            product.apportionmentValue,
            city.apportionmentPercentage
          );
          items.push({
            ...productData,
            description,
            unitCost: measurementUnitCost,
            apportionmentType: EApportionmentType.MEASUREMENTANDREADJUST,
          });
        }
      }
    }

    return items;
  }

  /** Calcula o valor da medição e valor de reajuste separados
   * para um item.
   */
  private calculateItemWithSeparateReadjust(
    productMeasurementValue: number,
    cityApportionmentPercentage: number = 0,
    measurementReadjustedPercentage: number
  ) {
    const measurementUnitCost = percentualToValue(
      cityApportionmentPercentage,
      productMeasurementValue
    );
    const readjustUnitCost = times(
      measurementUnitCost,
      measurementReadjustedPercentage
    );

    return {
      measurementUnitCost,
      readjustUnitCost,
    };
  }

  /** Calcula o valor de rateio do item considerando
   * o reajuste.
   */
  private calculateItem(
    productApportionmentValue: number = 0,
    cityApportionmentPercentage: number = 0
  ) {
    return percentualToValue(
      cityApportionmentPercentage,
      productApportionmentValue
    );
  }

  /** Calcula os valores de retenção e desconto por produto. */
  private calculateValuesToSend(
    measurement: MeasurementModel,
    itemsQuantity: number
  ) {
    const retention = decimal(measurement.getTechnicalRetentionTotal())
      .dividedBy(itemsQuantity)
      .toNumber();
    const discount = decimal(measurement.getAllDiscountsTotal())
      .sum(measurement.getAdvancesTotal())
      .dividedBy(itemsQuantity)
      .toNumber();

    return {
      discount,
      retention,
    };
  }

  /** Cria o texto que será enviado como observação no envio para faturamento. */
  private createObservationToSend(measurement: MeasurementModel) {
    return `Pedido gerado pelo Rubk Faturamento. Valor da Nota Fiscal: R$ ${formatNumber(
      measurement.getGrossTotal(),
      2
    )}`;
  }
}

export default new SendToBillingService();
